import React, { useState, useEffect } from "react";
import { Tabs, Tab, Box, IconButton, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Generic "list of tabbed rows" form.
 *
 * Fully controlled: the caller owns `rows` (an array of plain objects) and
 * receives updates via `onRowsChange`. This component owns nothing but the
 * (optional) active-tab index, so there is exactly one source of truth for
 * the data — no internal/external state to keep in sync.
 *
 * Used for any "add multiple items of type X, each with a few fields"
 * section — Racks, Baskets, Hooks, Hampers all use this same component,
 * just with a different `renderRow` and `createEmptyRow`.
 *
 * Props:
 *  - rows: array of row objects (required, controlled)
 *  - onRowsChange(nextRows): called with the full next array on any change
 *  - renderRow(row, index, onFieldChange): must render the fields for one row.
 *      onFieldChange(index, patch) applies a partial update, e.g.
 *      onFieldChange(index, { type: "X", code: "", colour: "" })
 *  - createEmptyRow(): returns a fresh blank row object for "Add"
 *  - labelPrefix: e.g. "Rack" -> tabs read "Rack 1", "Rack 2", button reads "Add Rack"
 *  - activeTab / onActiveTabChange: optional, lets a parent (e.g. a
 *      validator that wants to jump to the first invalid tab) control which
 *      tab is shown. If omitted, the component manages this itself.
 */
export default function DynamicTabsForm({
  rows,
  onRowsChange,
  renderRow,
  createEmptyRow,
  labelPrefix = "Item",
  activeTab: controlledActiveTab,
  onActiveTabChange,
}) {
  const [internalActiveTab, setInternalActiveTab] = useState(0);
  const isControlled = controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : internalActiveTab;
  const setActiveTab = isControlled ? onActiveTabChange : setInternalActiveTab;

  // Keep the shown tab in range if rows shrink/grow from outside
  // (e.g. reset, or loading a saved job).
  useEffect(() => {
    if (rows.length === 0 && activeTab !== 0) {
      setActiveTab(0);
    } else if (activeTab >= rows.length && rows.length > 0) {
      setActiveTab(rows.length - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);

  const handleAdd = () => {
    onRowsChange([...rows, createEmptyRow()]);
    setActiveTab(rows.length); // jump to the newly added (last) tab
  };

  const handleRemove = (index) => {
    const updated = rows.filter((_, i) => i !== index);
    onRowsChange(updated);
    if (activeTab >= updated.length) {
      setActiveTab(Math.max(updated.length - 1, 0));
    } else if (activeTab > index) {
      setActiveTab(activeTab - 1);
    }
  };

  const handleFieldChange = (index, patch) => {
    const updated = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onRowsChange(updated);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Button
        variant="contained"
        sx={{ mb: 2, backgroundColor: "#352c1d", color: "white" }}
        onClick={handleAdd}
      >
        Add {labelPrefix}
      </Button>

      {rows.length > 0 && (
        <>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {rows.map((_, index) => (
              <Tab
                key={index}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {labelPrefix} {index + 1}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(index);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
              />
            ))}
          </Tabs>

          <Box sx={{ p: 2, border: "1px solid #ddd", mt: 2 }}>
            {renderRow(rows[activeTab], activeTab, handleFieldChange)}
          </Box>
        </>
      )}
    </Box>
  );
}
